'use client'

import { memo, useEffect, useMemo, useState, useTransition } from 'react'
import ReactMarkdown from 'react-markdown'
import type { ComponentProps } from 'react'
import { useTranslations } from 'next-intl'
import { markdownComponents } from '@/components/markdown-components'
import {
  markdownPreviewRehypePlugins,
  markdownPreviewRemarkPlugins,
} from '@/lib/markdown/preview-plugins'
import { normalizeChatGptMathDelimiters } from '@/lib/markdown/math-delimiters'

// 输入始终同步更新；只有预览快照防抖，下载不能使用这个快照。
export function useMarkdownPreview(markdown: string, enabled = true) {
  const [previewMarkdown, setPreviewMarkdown] = useState('')
  const [isPending, startTransition] = useTransition()

  useEffect(() => {
    if (!enabled) return
    const timer = setTimeout(() => {
      startTransition(() => setPreviewMarkdown(markdown))
    }, 400)
    return () => clearTimeout(timer)
  }, [markdown, enabled])

  return {
    previewMarkdown,
    pending: isPending || previewMarkdown !== markdown,
  }
}

// 缓存整棵预览，避免编辑器每次按键都重新执行 Markdown 解析。
export const MarkdownEditorPreview = memo(function MarkdownEditorPreview({
  markdown,
  remarkPlugins = markdownPreviewRemarkPlugins,
  rehypePlugins = markdownPreviewRehypePlugins,
}: {
  markdown: string
  remarkPlugins?: ComponentProps<typeof ReactMarkdown>['remarkPlugins']
  rehypePlugins?: ComponentProps<typeof ReactMarkdown>['rehypePlugins']
}) {
  const normalized = useMemo(
    () => normalizeChatGptMathDelimiters(markdown),
    [markdown],
  )
  return (
    <ReactMarkdown
      remarkPlugins={remarkPlugins}
      rehypePlugins={rehypePlugins}
      components={markdownComponents}
    >
      {normalized}
    </ReactMarkdown>
  )
})

export function MarkdownPreviewStatus({ pending }: { pending: boolean }) {
  const t = useTranslations('MarkdownEditorShared')
  return (
    <span role="status" className="text-xs font-normal text-muted-foreground">
      {pending ? t('previewUpdating') : ''}
    </span>
  )
}
